import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Theme, Typography, withStyles, WithStyles } from '@material-ui/core';
import { AxiosRequestConfig } from 'axios';
import { Button, DateRangePicker, MessageTemplate } from 'core';
import ContentTableLoader from 'core/AvailabilitySelector/ContentTableLoader';
import { concat, filter, get, isEmpty, map, omit, pickBy, uniq } from 'lodash';
import { axiosService, TAvailability, useAppointmentTypesByIdsV2 } from 'ui-model';
import { createKeys, InfiniteQueryOptions, useCreateInfiniteQuery } from 'ui-model/dist/CRUD';

import { UseSettingSchedulerDatesResponse } from 'hooks/useSettingSchedulerDates';

import ListAvailabilitiesGrouped, { ListAvailabilitiesGroupedProps } from './ListAvailabilitiesGrouped';

const messages = defineMessages({
  loadMore: {
    id: 'components.v2.QueryListAvailabilitiesGrouped.loadMore',
    defaultMessage: 'Load More',
  },
  speakWithStaff: {
    id: 'components.v2.QueryListAvailabilitiesGrouped.speakWithStaff',
    defaultMessage: 'Speak with Staff',
  },
  restartConversation: {
    id: 'components.v2.QueryListAvailabilitiesGrouped.restartConversation',
    defaultMessage: 'Restart Conversation',
  },
  noAppointmentsFound: {
    id: 'components.v2.QueryListAvailabilitiesGrouped.noAppointmentsFound',
    defaultMessage: '0 appointment times found. Try adjusting your filters to view more results.',
  },
});

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    loadMoreWrapper: {
      width: '100%',
      textAlign: 'center',
    },
    dateRangePickerContainer: {
      marginBottom: `${unit * 1.5}px`,
    },
  });

const keys = createKeys('availabilities');

type TData = {
  response: TAvailability[];
  appointmentTypes: string[];
  facilities: string[];
  providers: string[];
  page: number;
  size: number;
};

export type TDistance = {
  zipcode: string;
  zipcodeMaxDistance: number;
};

export type TFilters = {
  facilities: string[];
  providers: string[];
  distance: TDistance;
  appointmentTypes: string[];
};

// TODO: Move to `ui-model` once it's validated by QA in prod
const useAvailabilitySchedulerPagination = (
  initialParams?: AxiosRequestConfig['params'],
  options?: InfiniteQueryOptions<TData>,
) =>
  useCreateInfiniteQuery(
    keys.many(initialParams),
    async (params) => {
      const { data } = await axiosService.get('/v2/availabilities/scheduler', {
        params: get(params, 'zipcode', null) ? params : omit(params, 'zipcodeMaxDistance'),
      });
      return data as TData;
    },
    {
      limit: 100,
      sort: 'date',
      sortBy: 'asc',
      status: 'available',
      populate: true,
      includeNullApptTypes: true,
      ...pickBy(initialParams, (param) => param),
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.response.length < 100) return undefined;
        return allPages.length + 1;
      },
      // Avoid calls missing the date range param
      enabled: !!initialParams?.date,
      // @ts-ignore TODO: Expose option from `ui-model#InfiniteQueryOptions` type
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export interface QueryListAvailabilitiesGroupedProps
  extends Omit<ListAvailabilitiesGroupedProps, 'availabilities'>,
    WithStyles<typeof styles> {
  params: any;
  dateRange: UseSettingSchedulerDatesResponse;
  setDateRange: (params: UseSettingSchedulerDatesResponse) => void;
  isMobile?: boolean;
  showFilters?: boolean;
}

const QueryListAvailabilitiesGrouped: React.FC<QueryListAvailabilitiesGroupedProps> = ({
  classes,
  params,
  isMobile,
  showFilters,
  dateRange,
  setDateRange,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const {
    isLoading,
    isFetchingNextPage,
    data: dataPagination,
    hasNextPage,
    fetchNextPage,
  } = useAvailabilitySchedulerPagination(params);

  const availabilities = useMemo(() => concat(...map(dataPagination?.pages, 'response')), [dataPagination]);
  const apptTypeIds = useMemo(() => uniq(map(availabilities, (availability) => availability.appointmentType)), [
    availabilities,
  ]);

  const { data: appointmentTypes = [] } = useAppointmentTypesByIdsV2({
    ids: apptTypeIds,
    limit: apptTypeIds.length,
  });

  const populatedAvailabilities = useMemo(
    () =>
      map(availabilities, (availability) => ({
        ...availability,
        appointmentType: filter(
          appointmentTypes,
          (appointmentType) => appointmentType._id === availability.appointmentType,
        )[0],
      })),
    [availabilities, appointmentTypes],
  );

  return (
    <div>
      <div className={classes.dateRangePickerContainer}>
        <DateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onDatesChange={setDateRange}
          numberOfMonths={1}
        />
      </div>
      {!isLoading && isEmpty(populatedAvailabilities) ? (
        <Typography variant="h6">
          <MessageTemplate
            id={'messages.web.emptyAppointmentResultsFiltersAvailable'}
            defaultMessage={formatMessage(messages.noAppointmentsFound)}
          />
        </Typography>
      ) : (
        <>
          <ListAvailabilitiesGrouped availabilities={populatedAvailabilities} {...rest} />
          {(isLoading || isFetchingNextPage) && <ContentTableLoader isMobile={isMobile} />}
          {hasNextPage && !isLoading && !isFetchingNextPage && (
            <div className={classes.loadMoreWrapper}>
              <Button variant="contained" color="primary" fullWidth={isMobile} onClick={() => fetchNextPage()}>
                {formatMessage(messages.loadMore)}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default withStyles(styles)(QueryListAvailabilitiesGrouped);
